// Phone shell — what App renders instead of the sidebar layout when
// window.AtlasDevice.phone (src/device.jsx). Top bar (back · title · search ·
// Ask · +), the routed page, and a bottom tab bar (Today · Deals · Tasks ·
// Calendar · More). Navigation state, role gating and every overlay (⌘K,
// Ask Atlas, quick-create drawers, toasts) stay in App and are shared with
// the desktop shell; this file only lays them out for a phone.
//
// Also home to the small phone UI kit the Phone* pages build on
// (PhoneSheet, PhoneScroll, PhoneRow, PhoneSegmented, PhoneSearchField,
// PhoneFilterButton, PhoneSectionHeader, PhoneEmpty, BestOnDesktop) — all on window.*.

// ── Page titles ────────────────────────────────────────────────────────────
const PHONE_TITLES = {
  home: 'Today', inbox: 'Tasks', calendar: 'Calendar', deals: 'Engagements', deal: 'Engagement',
  companies: 'Companies', company: 'Company', people: 'People', contact: 'Person', notes: 'Notes',
  files: 'Files', referrals: 'Referrals', 'referral-partners': 'Referral Partners', gpp: 'GPP Partners',
  events: 'Events', buyers: 'Buyers', buyer: 'Buyer', calling: 'Calling', marketing: 'Emails', blast: 'Blast',
  cleanup: 'Needs cleaning', analytics: 'Analytics', views: 'Analytics', view: 'Saved view', signals: 'Signals',
  agents: 'Agents', workspace: 'Agent', 'lost-deals': 'Lost Deals', 'team-pulse': 'Team activity',
  'daily-reports': 'Team activity', activity: 'Data audit', settings: 'Settings', user: 'Team member',
  record: 'Opening…', cim: 'CIM', industries: 'Industries', industry: 'Industry', ask: 'Ask Atlas',
};

// What a phone can do instead on a desktop-only page, shown on its "built for
// a bigger screen" card: one true line and, optionally, one action that gets
// there. action: 'search' (the ⌘K palette) · 'ask' (Ask Atlas) · 'deals' (the
// Deals tab) · 'report' (the daily-report reader below). label overrides the
// action's default button text. A body that points at the action is only
// true when the role can take it, so without the action the card shows no
// hint — a caller keeps just Calling, with no search or contact pages.
const PHONE_HINTS = {
  buyers: { body: 'Looking up one firm or person? Search for them.', action: 'search' },
  gpp: { body: 'Looking up one firm or person? Search for them.', action: 'search' },
  'referral-partners': { body: 'Looking up one firm or person? Search for them.', action: 'search' },
  referrals: { body: 'Looking up one firm or person? Search for them.', action: 'search' },
  calling: { body: 'Looking up one contact? Search for them.', action: 'search' },
  cleanup: { body: 'To fix one stale deal, open it from Deals.', action: 'deals' },
  files: { body: 'Looking for one deal\'s or company\'s files? Search for it.', action: 'search' },
  analytics: { body: 'For a quick answer, ask Atlas.', action: 'ask' },
  views: { body: 'For a quick answer, ask Atlas.', action: 'ask' },
  view: { body: 'For a quick answer, ask Atlas.', action: 'ask' },
  'daily-reports': { action: 'report', label: 'Read this report' },
  'team-pulse': { action: 'report', label: 'Read the daily report' },
  settings: { body: 'Appearance and team scope are under More.' },
};

// Pages with a phone-built version: the view id → the window.* component
// that renders it. A page missing here (or whose component isn't loaded)
// renders its desktop view as-is, or the "best on desktop" card below.
const PHONE_VIEWS = {
  home: 'PhoneToday', inbox: 'PhoneTasks', calendar: 'PhoneCalendar',
  deals: 'PhoneDeals', companies: 'PhoneCompanies', people: 'PhonePeople',
};
// Pages that render their desktop view in place on a phone (they adapt
// themselves to window.AtlasDevice.phone): the record pages and a few
// read-mostly lists.
const PHONE_IN_PLACE = new Set([
  'deal', 'company', 'contact', 'notes', 'user', 'buyer', 'record', 'deals', 'activity', 'workspace', 'signals', 'cim',
  'industries', 'industry', 'ask',
]);
// Everything else is an admin / analysis / marketing surface — on a phone it
// gets a "best on desktop" card with an escape hatch to open it anyway.

const tap = { WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' };

// ── PhoneSheet — bottom sheet (action sheets, pickers, More) ────────────────
// `height` pins the panel's height (a reader that shouldn't jump as it
// loads); by default it hugs its content up to maxHeight.
const PhoneSheet = ({ open, onClose, title, children, footer, maxHeight = '86%', height, zIndex = 120 }) => {
  const panelRef = React.useRef(null);
  const backdropRef = React.useRef(null);
  const [mounted, setMounted] = React.useState(open);
  const openRef = React.useRef(open);
  openRef.current = open;
  React.useEffect(() => {
    if (open) { setMounted(true); return; }
    if (!panelRef.current) { setMounted(false); return; }
    Promise.all([
      window.animateEl(panelRef.current, { transform: 'translateY(40px)', opacity: 0 }),
      window.animateEl(backdropRef.current, { opacity: 0 }),
    ]).then(() => { if (!openRef.current) setMounted(false); });
  }, [open]);
  React.useLayoutEffect(() => {
    if (!open || !mounted) return;
    window.animateEl(panelRef.current, { transform: ['translateY(40px)', 'translateY(0px)'], opacity: [0, 1] }, { duration: 0.2 });
    window.animateEl(backdropRef.current, { opacity: [0, 1] });
  }, [open, mounted]);
  window.useEscapeKey(open, onClose);
  if (!mounted) return null;
  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', pointerEvents: open ? 'auto' : 'none' }}>
      <div ref={backdropRef} onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'oklch(0.2 0.01 270 / 0.32)' }} />
      <div ref={panelRef} role="dialog" aria-modal="true" className="atlas-sheet" style={{
        position: 'relative', maxHeight, height, marginBottom: 'var(--kb, 0px)', display: 'flex', flexDirection: 'column',
        background: 'var(--surface)', borderRadius: '16px 16px 0 0', boxShadow: 'var(--shadow-lg)',
        paddingBottom: footer ? 0 : 'var(--sab)', paddingLeft: 'var(--sal)', paddingRight: 'var(--sar)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '7px 0 2px', flexShrink: 0 }}>
          <span style={{ width: 36, height: 5, borderRadius: 3, background: 'var(--border-strong)' }} />
        </div>
        {title && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px 8px 18px', flexShrink: 0 }}>
            <div style={{ flex: 1, minWidth: 0, fontSize: 17, fontWeight: 600, color: 'var(--text)', fontFamily: 'var(--font-display)' }}>{title}</div>
            <button type="button" onClick={onClose} style={{ ...tap, height: 44, minWidth: 44, padding: '0 12px', border: 'none', background: 'transparent', color: 'var(--accent-text)', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>Done</button>
          </div>
        )}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>{children}</div>
        {footer && (
          <div style={{ flexShrink: 0, borderTop: '1px solid var(--border)', padding: '10px 16px', paddingBottom: 'calc(10px + var(--sab))' }}>{footer}</div>
        )}
      </div>
    </div>,
    document.body
  );
};

// Action-sheet rows: [{ label, sub, icon, onClick, danger, disabled }]
const PhoneActionList = ({ items }) => (
  <div style={{ padding: '0 0 8px' }}>
    {items.filter(Boolean).map((it, i) => (
      <button key={it.key || it.label || i} type="button" disabled={it.disabled}
        onClick={it.onClick}
        style={{
          ...tap, display: 'flex', alignItems: 'center', gap: 14, width: '100%', minHeight: 52,
          padding: '8px 18px', border: 'none', borderTop: i ? '1px solid var(--border)' : 'none',
          background: 'transparent', textAlign: 'left', cursor: 'pointer', opacity: it.disabled ? 0.45 : 1,
          color: it.danger ? 'var(--rose-fg)' : 'var(--text)',
        }}>
        {it.icon && <span style={{ display: 'inline-flex', color: it.danger ? 'var(--rose-fg)' : 'var(--text-3)', flexShrink: 0 }}>{it.icon}</span>}
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 16 }}>{it.label}</span>
          {it.sub && <span style={{ display: 'block', fontSize: 13, color: 'var(--text-3)', marginTop: 1 }}>{it.sub}</span>}
        </span>
        {it.trailing}
      </button>
    ))}
  </div>
);

// ── Layout kit for Phone* pages ─────────────────────────────────────────────
// The page scroller. Tapping the active tab again scrolls it to the top.
const PhoneScroll = React.forwardRef(({ children, style, onScroll }, fwd) => {
  const ref = React.useRef(null);
  React.useImperativeHandle(fwd, () => ref.current);
  React.useEffect(() => {
    const top = () => { try { ref.current && ref.current.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) {} };
    window.addEventListener('atlas-tab-reselect', top);
    return () => window.removeEventListener('atlas-tab-reselect', top);
  }, []);
  return (
    <div ref={ref} onScroll={onScroll} style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', paddingBottom: 24, ...style }}>
      {children}
    </div>
  );
});

const PhoneSectionHeader = ({ title, count, action, style }) => (
  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '18px 16px 6px', ...style }}>
    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {title}{count != null && <span style={{ marginLeft: 6, color: 'var(--text-4)', fontWeight: 500 }}>{count}</span>}
    </div>
    <div style={{ flex: 1 }} />
    {action}
  </div>
);

// A list row: leading (avatar/logo/checkbox) · title + subtitle · meta · chevron.
const PhoneRow = ({ leading, title, subtitle, meta, trailing, onClick, chevron = !!onClick, dim = false, style }) => {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag type={onClick ? 'button' : undefined} onClick={onClick}
      style={{
        ...tap, display: 'flex', alignItems: 'center', gap: 12, width: '100%', minHeight: 56,
        padding: '10px 16px', border: 'none', borderBottom: '1px solid var(--border)',
        background: 'var(--surface)', textAlign: 'left', cursor: onClick ? 'pointer' : 'default',
        opacity: dim ? 0.6 : 1, color: 'var(--text)', fontFamily: 'inherit', ...style,
      }}>
      {leading && <span style={{ flexShrink: 0, display: 'inline-flex' }}>{leading}</span>}
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 15.5, fontWeight: 500, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
        {subtitle && <span style={{ display: 'block', fontSize: 13, color: 'var(--text-3)', lineHeight: 1.35, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subtitle}</span>}
      </span>
      {meta != null && <span style={{ flexShrink: 0, fontSize: 13, color: 'var(--text-3)', textAlign: 'right' }}>{meta}</span>}
      {trailing}
      {chevron && <span style={{ flexShrink: 0, display: 'inline-flex', color: 'var(--text-4)' }}><Icon.ChevronR size={14} /></span>}
    </Tag>
  );
};

// iOS-style segmented control. options: [{ value, label, count }]
const PhoneSegmented = ({ value, options, onChange, style }) => (
  <div role="tablist" style={{ display: 'flex', gap: 2, padding: 2, borderRadius: 10, background: 'var(--surface-2)', border: '1px solid var(--border)', ...style }}>
    {options.map((o) => {
      const on = o.value === value;
      return (
        <button key={o.value} type="button" role="tab" aria-selected={on} onClick={() => onChange(o.value)}
          style={{
            ...tap, flex: 1, minWidth: 0, height: 34, borderRadius: 8, border: 'none', cursor: 'pointer',
            background: on ? 'var(--surface)' : 'transparent', boxShadow: on ? 'var(--shadow-sm)' : 'none',
            color: on ? 'var(--text)' : 'var(--text-2)', fontSize: 14, fontWeight: on ? 600 : 500,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '0 6px',
          }}>
          {o.label}{o.count != null && <span style={{ marginLeft: 5, color: 'var(--text-3)', fontWeight: 500 }}>{o.count}</span>}
        </button>
      );
    })}
  </div>
);

// Horizontally scrolling filter chips. options: [{ value, label, count }]
const PhoneChips = ({ value, options, onChange, style }) => (
  <div style={{ display: 'flex', gap: 8, overflowX: 'auto', padding: '2px 16px', scrollbarWidth: 'none', ...style }}>
    {options.map((o) => {
      const on = o.value === value;
      return (
        <button key={o.value} type="button" onClick={() => onChange(o.value)}
          style={{
            ...tap, flexShrink: 0, height: 34, padding: '0 14px', borderRadius: 999, cursor: 'pointer',
            border: '1px solid ' + (on ? 'var(--accent)' : 'var(--border)'),
            background: on ? 'var(--accent-soft)' : 'var(--surface)', color: on ? 'var(--accent-text)' : 'var(--text-2)',
            fontSize: 14, fontWeight: on ? 600 : 500, whiteSpace: 'nowrap',
          }}>
          {o.label}{o.count != null && <span style={{ marginLeft: 5, opacity: 0.75 }}>{o.count}</span>}
        </button>
      );
    })}
  </div>
);

const PhoneSearchField = ({ value, onChange, placeholder = 'Search', autoFocus = false, style }) => (
  <div style={{ position: 'relative', ...style }}>
    <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', display: 'inline-flex', pointerEvents: 'none' }}>
      <Icon.Search size={16} />
    </span>
    <input type="search" enterKeyHint="search" autoCapitalize="off" autoCorrect="off" spellCheck={false}
      autoFocus={autoFocus} value={value} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: '100%', height: 40, padding: '0 36px 0 34px', fontSize: 16, borderRadius: 10,
        border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text)', outline: 'none',
        WebkitAppearance: 'none', appearance: 'none',
      }} />
    {value && (
      <button type="button" aria-label="Clear search" onClick={() => onChange('')}
        style={{ ...tap, position: 'absolute', right: 2, top: 0, bottom: 0, width: 36, border: 'none', background: 'transparent', color: 'var(--text-3)', fontSize: 18, cursor: 'pointer' }}>×</button>
    )}
  </div>
);

// "Filters" beside a page's search field; it opens the page's filter sheet.
// `count` = filters on inside that sheet (badge + accent border).
const PhoneFilterButton = ({ count = 0, onClick }) => (
  <button type="button" onClick={onClick} aria-label={count ? `Filters, ${count} on` : 'Filters'}
    style={{
      ...tap, height: 40, padding: '0 12px', flexShrink: 0, borderRadius: 10, cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 15, fontWeight: 500, fontFamily: 'inherit',
      border: '1px solid ' + (count ? 'var(--accent)' : 'var(--border)'),
      background: count ? 'var(--accent-soft)' : 'var(--surface)',
      color: count ? 'var(--accent-text)' : 'var(--text-2)',
    }}>
    <Icon.Filter size={16} />
    Filters
    {count > 0 && (
      <span className="num" style={{
        minWidth: 20, height: 20, padding: '0 6px', borderRadius: 10, background: 'var(--accent)', color: '#fff',
        fontSize: 12, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}>{count}</span>
    )}
  </button>
);

const PhoneEmpty = ({ title, body, action }) => (
  <div style={{ padding: '40px 28px', textAlign: 'center' }}>
    <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-2)' }}>{title}</div>
    {body && <div style={{ fontSize: 14, color: 'var(--text-3)', marginTop: 6, lineHeight: 1.45 }}>{body}</div>}
    {action && <div style={{ marginTop: 14 }}>{action}</div>}
  </div>
);

// ── Landing counter ─────────────────────────────────────────────────────────
// Which desktop-only pages people reach for on a phone, and what they do on
// the card: 'card' (it showed), 'open_anyway', 'hint' (the hint's action).
// One fire-and-forget beacon per view + action per session — sessionStorage,
// plus an in-memory set for when storage is blocked. Nothing renders off it,
// and it carries the view id only, never the URL.
const LANDING_KEY = 'atlas.phoneLanding';
const _landingSent = new Set();
const sendLanding = (view, action) => {
  const d = window.AtlasDevice || {};
  const device = d.phone ? 'phone' : d.tablet ? 'tablet' : null;
  if (!device || !/^[a-z][a-z-]{0,39}$/.test(view || '')) return;
  const key = view + ':' + action;
  if (_landingSent.has(key)) return;
  _landingSent.add(key);
  try {
    const seen = JSON.parse(sessionStorage.getItem(LANDING_KEY) || '[]');
    if (Array.isArray(seen)) {
      if (seen.includes(key)) return;
      sessionStorage.setItem(LANDING_KEY, JSON.stringify([...seen, key]));
    }
  } catch (_) {}
  try { if (window.AtlasApi && window.AtlasApi.phoneLanding) window.AtlasApi.phoneLanding(view, action, device); } catch (_) {}
};

// ── Daily report reader ─────────────────────────────────────────────────────
// Atlas posts #/daily-reports?date=… and ?tab=book links to Slack every
// weekday. On a phone the card opens the server-rendered email for that day
// (or Book health) in a sandboxed frame: no scripts run inside it. An Atlas
// link in it closes the sheet and opens in-app, and Back from that record
// reopens the report where it was left. Anything else opens outside.
//
// Two layers, because WebKit (iOS Safari and the iOS shell) never calls a
// listener the parent adds inside a frame whose scripts are sandboxed off —
// Chrome does. So the links are also rewritten before the frame renders, to
// work with no script at all: an Atlas link becomes this page's own URL +
// its hash with target=_top (a same-document hash change the app routes on),
// a web link gets target=_blank, mail/phone links target=_top, anything else
// loses its href. In the iOS shell Capacitor hands a _blank link to iOS —
// Safari, or the Outlook app for the report's Outlook links, the same place
// AtlasNative.openExternal sends Microsoft 365 hosts. Chrome's listener
// calls openExternal itself.

// Reports fetched this session (a stored day doesn't change; "latest" is
// refetched after half an hour), and the reader to reopen when Back comes
// back from a record opened out of it.
const REPORT_CACHE_MS = 30 * 60 * 1000;
const _reportCache = new Map();   // reportKey → { html, filename, at }
let _reportResume = null;         // { hash, date, book, scrollTop } — the card's hash
const reportKey = (date, book) => (book ? 'book:' : 'daily:') + (date || 'latest');

// Same params as views/dealmaker_daily.jsx; no date = the server's latest day.
const reportFromHash = () => {
  const h = window.location.hash || '';
  const d = h.match(/[?&]date=(\d{4}-\d{2}-\d{2})/);
  const t = h.match(/[?&]tab=(daily|book|cleanup)/);
  return { date: d ? d[1] : null, book: !!t && t[1] === 'book' };
};
const reportDayLabel = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
  if (!m) return null;
  return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};
// The email is laid out for a 760px column: tighten the gutter and let the
// wide numbers table scroll sideways on its own.
const REPORT_PHONE_CSS = '<style>body{padding:12px}table.bench{display:block;overflow-x:auto;-webkit-overflow-scrolling:touch}</style>';
// A link into Atlas (a bare #/… or a full URL on this app's address) → the
// in-app hash to open; anything else → null.
const reportAtlasHash = (href) => {
  if (/^#\//.test(href)) return href;
  let u = null;
  try { u = new URL(href, window.location.href); } catch (_) { return null; }
  if (!/^https?:$/.test(u.protocol)) return null;
  const origins = [window.location.origin];
  try { if (window.__ATLAS_PUBLIC_URL__) origins.push(new URL(window.__ATLAS_PUBLIC_URL__).origin); } catch (_) {}
  if (!origins.includes(u.origin)) return null;
  return /^#\//.test(u.hash) ? u.hash : '#/';
};
// Web, mail and phone links only — never javascript:/data: out of the report.
const reportOutsideUrl = (href) => {
  let u = null;
  try { u = new URL(href, window.location.href); } catch (_) { return null; }
  return /^(https?|mailto|tel):$/.test(u.protocol) ? u : null;
};
const reportOpenOutside = (href) => {
  const u = reportOutsideUrl(href);
  if (!u) return;
  if (window.AtlasNative && window.AtlasNative.isNative && window.AtlasNative.openExternal) { window.AtlasNative.openExternal(u.href); return; }
  if (u.protocol !== 'http:' && u.protocol !== 'https:') { window.location.href = u.href; return; }
  window.open(u.href, '_blank', 'noopener');
};
// The script-free layer (see above). DOMParser output is inert — nothing in
// it runs or loads here.
const prepareReportHtml = (html) => {
  let doc = null;
  try { doc = new DOMParser().parseFromString(html || '', 'text/html'); } catch (_) {}
  if (!doc || !doc.documentElement) return REPORT_PHONE_CSS + (html || '');
  doc.querySelectorAll('script, base, meta[http-equiv]').forEach((n) => n.remove());
  const here = window.location.origin + window.location.pathname + window.location.search;
  doc.querySelectorAll('a[href]').forEach((a) => {
    const href = a.getAttribute('href') || '';
    const hash = reportAtlasHash(href);
    // The report's own "Open in Atlas" (#/daily-reports…) only lands back on
    // this card — on iOS, the very same URL, so the tap does nothing at all.
    if (hash && /^#\/daily-reports(?:[?/]|$)/.test(hash)) { a.remove(); return; }
    if (hash) { a.setAttribute('href', here + hash); a.setAttribute('target', '_top'); return; }
    const u = reportOutsideUrl(href);
    if (!u) { a.removeAttribute('href'); return; }
    a.setAttribute('href', u.href);
    a.setAttribute('target', /^https?:$/.test(u.protocol) ? '_blank' : '_top');
    a.setAttribute('rel', 'noopener noreferrer');
  });
  if (doc.head) doc.head.insertAdjacentHTML('beforeend', REPORT_PHONE_CSS);
  return '<!doctype html>' + doc.documentElement.outerHTML;
};

const DailyReportSheet = ({ open, onClose, date, book, cardHash, resumeScroll = 0 }) => {
  const [state, setState] = React.useState({ status: 'loading' });
  const [attempt, setAttempt] = React.useState(0);
  const [frameH, setFrameH] = React.useState(0);
  // A fresh frame per open: a cached report reopened mid-close-animation
  // would otherwise keep the old frame, fire no load, and never re-measure.
  const [openId, setOpenId] = React.useState(0);
  const frameRef = React.useRef(null);
  const pendingScroll = React.useRef(0);
  // What to reopen on the way back (read when the card unmounts).
  const memo = React.useRef({});
  memo.current = { date, book, cardHash };
  const openRef = React.useRef(open);
  openRef.current = open;

  // The sheet's own scroller (PhoneSheet's body) holds the frame. Its
  // position is tracked as it moves: by the time the card unmounts it's gone.
  const scrollerRef = React.useRef(null);
  const scrollTopRef = React.useRef(0);
  const trackScroll = React.useRef(() => { if (scrollerRef.current) scrollTopRef.current = scrollerRef.current.scrollTop; }).current;
  const wrapRef = React.useCallback((node) => {
    if (scrollerRef.current) scrollerRef.current.removeEventListener('scroll', trackScroll);
    scrollerRef.current = node ? node.parentElement : null;
    if (scrollerRef.current) scrollerRef.current.addEventListener('scroll', trackScroll, { passive: true });
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!open) return undefined;
    let stale = false;
    setFrameH(0);
    setOpenId((n) => n + 1);
    pendingScroll.current = resumeScroll || 0;
    scrollTopRef.current = 0;
    const key = reportKey(date, book);
    const hit = _reportCache.get(key);
    if (attempt === 0 && hit && Date.now() - hit.at < REPORT_CACHE_MS) {
      setState({ status: 'ready', html: hit.html, filename: hit.filename });
      return undefined;
    }
    setState({ status: 'loading' });
    const api = window.AtlasApi;
    const load = api && (book ? api.bookHealthHtml : api.dailyReportHtml);
    (load ? load(date || undefined) : Promise.reject(new Error('unavailable')))
      .then((r) => {
        const html = (r && r.html) || '';
        const filename = (r && r.filename) || '';
        _reportCache.set(key, { html, filename, at: Date.now() });
        if (_reportCache.size > 6) _reportCache.delete(_reportCache.keys().next().value);
        if (!stale) setState({ status: 'ready', html, filename });
      })
      .catch((e) => { if (!stale) setState({ status: 'error', code: (e && e.status) || 0 }); });
    return () => { stale = true; };
  }, [open, date, book, attempt]);   // eslint-disable-line react-hooks/exhaustive-deps

  // The frame grows to the email's height so the sheet is the only scroller
  // (no scroll-inside-a-scroll on iOS). Needs allow-same-origin to read it.
  const measure = () => {
    let doc = null;
    try { doc = frameRef.current && frameRef.current.contentDocument; } catch (_) {}
    if (doc && doc.body) setFrameH(Math.ceil(doc.body.getBoundingClientRect().height) + 2);
  };
  React.useEffect(() => {
    if (state.status !== 'ready') return undefined;
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [state.status]);   // eslint-disable-line react-hooks/exhaustive-deps
  // Back from a record: once the frame is tall enough, scroll to where the
  // reader was (a late re-measure gets another try).
  React.useEffect(() => {
    const el = scrollerRef.current;
    const want = pendingScroll.current;
    if (!el || !want || !frameH) return;
    el.scrollTop = want;
    if (el.scrollHeight - el.clientHeight >= want - 2) pendingScroll.current = 0;
  }, [frameH]);

  // An Atlas link in the report (either path) changes the hash, and the new
  // route unmounts the card with the reader still open — a hashchange
  // listener here would be too late: the app's own runs first and React
  // unmounts this before ours is called. So the unmount is the signal:
  // remember the report and the scroll so the card reopens it on the way
  // back. Done / the backdrop close it first, and nothing is kept.
  React.useEffect(() => () => {
    const m = memo.current;
    if (!openRef.current || (window.location.hash || '') === (m.cardHash || '')) return;
    _reportResume = { hash: m.cardHash || '', date: m.date || null, book: !!m.book, scrollTop: scrollTopRef.current };
  }, []);

  const onFrameLoad = () => {
    let doc = null;
    try { doc = frameRef.current && frameRef.current.contentDocument; } catch (_) {}
    if (!doc) return;
    // Chrome path (see above); WebKit falls through to the rewritten links.
    doc.addEventListener('click', (e) => {
      const a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
      if (!a) return;
      e.preventDefault();
      const href = a.getAttribute('href') || '';
      const hash = reportAtlasHash(href);
      if (hash) { window.location.hash = hash; return; }
      reportOpenOutside(href);
    });
    measure();
    setTimeout(measure, 300);   // late layout (fonts) settling
  };

  const srcDoc = React.useMemo(() => (state.status === 'ready' ? prepareReportHtml(state.html) : null), [state]);

  const fileDay = state.filename ? (/(\d{4}-\d{2}-\d{2})/.exec(state.filename) || [])[1] : null;
  const dayLabel = reportDayLabel(date || fileDay);
  const title = (book ? 'Book health' : 'Daily report') + (dayLabel ? ' · ' + dayLabel : '');

  let body;
  if (state.status === 'ready') {
    body = (
      // Matches the email's own page colour so a short report doesn't end in a seam.
      <div ref={wrapRef} style={{ minHeight: '100%', background: '#f6f7f9' }}>
        <iframe key={openId} ref={frameRef} title={title} srcDoc={srcDoc} sandbox="allow-same-origin allow-top-navigation-by-user-activation allow-popups allow-popups-to-escape-sandbox"
          scrolling="no" onLoad={onFrameLoad}
          style={{ display: 'block', border: 'none', width: 1, minWidth: '100%', height: frameH || '100%', minHeight: frameH ? 0 : 400 }} />
      </div>
    );
  } else if (state.status === 'error') {
    const retry = (
      <button type="button" onClick={() => setAttempt((n) => n + 1)}
        style={{ ...tap, height: 40, padding: '0 18px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: 15, fontWeight: 500, cursor: 'pointer' }}>
        Try again
      </button>
    );
    body = state.code === 403 ? <PhoneEmpty title="You don't have access to team reports" />
      : state.code === 404 ? <PhoneEmpty title="No report for that day" body="Nothing was saved for this date." />
      : <PhoneEmpty title="Couldn't load the report" body="Check your connection and try again." action={retry} />;
  } else {
    body = <div style={{ padding: '48px 0', textAlign: 'center', fontSize: 14.5, color: 'var(--text-3)' }}>Loading the report…</div>;
  }

  return (
    <PhoneSheet open={open} onClose={onClose} title={title}
      height="calc(100% - var(--sat) - 12px)" maxHeight="calc(100% - var(--sat) - 12px)">
      {body}
    </PhoneSheet>
  );
};

// ── Best on desktop ─────────────────────────────────────────────────────────
const BestOnDesktop = ({ view, onOpenAnyway, setView }) => {
  // Back from a record opened in the reader lands here again: reopen the
  // report it came from, at the same spot (once — it's used up on mount).
  const [resume] = React.useState(() => (_reportResume && _reportResume.hash === (window.location.hash || '') ? _reportResume : null));
  const [report, setReport] = React.useState(resume);   // { date, book, hash, scrollTop } last opened (kept while the sheet animates out)
  const [reportOpen, setReportOpen] = React.useState(!!resume);
  React.useEffect(() => { if (resume && _reportResume === resume) _reportResume = null; }, [resume]);
  const url = (window.__ATLAS_PUBLIC_URL__ || window.location.origin) + '/' + (window.location.hash || '');
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); window.pushToast({ type: 'success', title: 'Link copied' }); }
    catch (_) { window.pushToast({ type: 'error', title: 'Could not copy the link' }); }
  };
  React.useEffect(() => { sendLanding(view, 'card'); }, [view]);

  // The hint's action, when this role can take it. Search and Ask go through
  // the same window events the sidebar and record pages use; Deals is the
  // tab bar's own switchView.
  const canGo = (page) => !window.AtlasCanSee || window.AtlasCanSee(page);
  const go = setView || (window.AtlasNav && window.AtlasNav.switchView);
  const hint = PHONE_HINTS[view] || null;
  const ACTIONS = {
    search: { label: 'Search', ok: canGo('search'), run: () => window.dispatchEvent(new CustomEvent('open-cmd')) },
    ask: { label: 'Ask Atlas', ok: !window.AtlasIsGuest, run: () => window.dispatchEvent(new CustomEvent('ask-atlas', { detail: { query: '' } })) },
    deals: { label: 'Go to Deals', ok: !!go && canGo('deals'), run: () => go('deals') },
    report: {
      label: 'Read this report', ok: true,
      run: () => {
        const at = view === 'daily-reports' ? reportFromHash() : { date: null, book: false };
        setReport({ ...at, hash: window.location.hash || '', scrollTop: 0 });
        setReportOpen(true);
      },
    },
  };
  const act = hint && hint.action ? ACTIONS[hint.action] : null;
  const action = act && act.ok ? { ...act, label: hint.label || act.label } : null;
  // A line that points at an action this role can't take isn't true for it.
  const hintBody = !hint || (hint.action && !action) ? null : hint.body || null;
  const runHint = () => { sendLanding(view, 'hint'); action.run(); };
  const openAnyway = () => { sendLanding(view, 'open_anyway'); onOpenAnyway(); };
  const btn = (primary) => ({
    ...tap, height: 46, borderRadius: 12, fontSize: 16, cursor: 'pointer',
    border: primary ? 'none' : '1px solid var(--border)', background: primary ? 'var(--accent)' : 'var(--surface)',
    color: primary ? '#fff' : 'var(--text)', fontWeight: primary ? 600 : 500,
  });

  return (
    <PhoneScroll>
      <div style={{ padding: '48px 28px', textAlign: 'center' }}>
        <div style={{ width: 56, height: 56, borderRadius: 16, margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--accent-soft)', color: 'var(--accent-text)' }}>
          <Icon.Maximize size={24} />
        </div>
        <div style={{ fontSize: 19, fontWeight: 600, color: 'var(--text)', fontFamily: 'var(--font-display)' }}>{PHONE_TITLES[view] || 'This page'} is built for a bigger screen</div>
        <div style={{ fontSize: 14.5, color: 'var(--text-3)', marginTop: 8, lineHeight: 1.5 }}>
          Open it on a computer, or open it here and scroll sideways.
        </div>
        {hintBody && (
          <div style={{ marginTop: 20, maxWidth: 300, marginLeft: 'auto', marginRight: 'auto', padding: '12px 14px', borderRadius: 12, background: 'var(--surface-2)', border: '1px solid var(--border)', fontSize: 14.5, color: 'var(--text-2)', lineHeight: 1.45, textAlign: 'left' }}>
            {hintBody}
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: hintBody ? 14 : 24, maxWidth: 300, marginLeft: 'auto', marginRight: 'auto' }}>
          {action && <button type="button" onClick={runHint} style={btn(true)}>{action.label}</button>}
          <button type="button" onClick={openAnyway} style={btn(!action)}>Open anyway</button>
          <button type="button" onClick={copy} style={btn(false)}>Copy link</button>
        </div>
      </div>
      {report && (
        <DailyReportSheet open={reportOpen} onClose={() => setReportOpen(false)} date={report.date} book={report.book}
          cardHash={report.hash} resumeScroll={report.scrollTop} />
      )}
    </PhoneScroll>
  );
};

// ── PhoneRoute — picks what a phone renders for the current view ────────────
const PhoneRoute = ({ view, isGuest, desktopViews, props }) => {
  const [peek, setPeek] = React.useState({});
  const compName = !isGuest && PHONE_VIEWS[view];
  const Comp = compName && window[compName];
  if (Comp) return <Comp {...props} />;
  if (PHONE_IN_PLACE.has(view)) return <div style={{ height: '100%', overflow: 'hidden' }}>{desktopViews}</div>;
  if (peek[view]) {
    return (
      <div style={{ height: '100%', overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {/* A desktop page at desktop width: its rows keep their own hit geometry (no PhoneHit). */}
        <window.PhoneHitScope.Provider value={false}>
          <div style={{ minWidth: 1024, height: '100%' }}>{desktopViews}</div>
        </window.PhoneHitScope.Provider>
      </div>
    );
  }
  return <BestOnDesktop key={view} view={view} setView={props && props.setView} onOpenAnyway={() => setPeek((p) => ({ ...p, [view]: true }))} />;
};

// ── More sheet: the rest of the nav + account ──────────────────────────────
const MORE_GROUPS = [
  { label: 'Records', ids: ['companies', 'people', 'referrals', 'buyers', 'industries', 'notes', 'files'] },
  { label: 'Sourcing', ids: ['referral-partners', 'gpp', 'events', 'calling'] },
  { label: 'Marketing', ids: ['marketing', 'blast', 'cleanup'] },
  { label: 'Insights', ids: ['analytics', 'signals'] },
  { label: 'Admin', ids: ['team-pulse', 'activity'], admin: true },
];
const MORE_ICONS = {
  companies: Icon.Building, people: Icon.User, referrals: Icon.Users, buyers: Icon.Diamond, notes: Icon.Doc,
  files: Icon.Folder, 'referral-partners': Icon.Target, gpp: Icon.Users, events: Icon.Pin, calling: Icon.Phone,
  marketing: Icon.Mail, blast: Icon.Mail, cleanup: Icon.Sparkle, analytics: Icon.Grid, signals: Icon.Activity,
  'team-pulse': Icon.Users, activity: Icon.Activity, settings: Icon.Settings, industries: Icon.Layers,
};
const onPhone = (id) => !!PHONE_VIEWS[id] || PHONE_IN_PLACE.has(id);

const PhoneMoreSheet = ({ open, onClose, canSee, isAdmin, flags, activeUser, teamMembers, onSwitchUser, theme, onToggleTheme, onNavigate, onSignOut, currentView }) => {
  const [pickUser, setPickUser] = React.useState(false);
  const flagOk = (id) => (id === 'calling' ? flags.canUseDialer : id === 'blast' ? flags.canManageBlasts : id === 'signals' ? flags.canViewSignals : true);
  const allTeams = window.AtlasApi?.isAllTeams?.() || false;
  const setScope = (wantsAll) => {
    if (wantsAll === allTeams) return;
    window.AtlasApi?.setAllTeams?.(wantsAll);
    window.location.reload();
  };
  const row = (id) => {
    const I = MORE_ICONS[id] || Icon.Dot;
    const active = currentView === id;
    return (
      <button key={id} type="button" onClick={() => onNavigate(id)}
        style={{ ...tap, display: 'flex', alignItems: 'center', gap: 14, width: '100%', minHeight: 50, padding: '8px 18px', border: 'none', background: active ? 'var(--surface-hover)' : 'transparent', textAlign: 'left', cursor: 'pointer', color: 'var(--text)' }}>
        <span style={{ display: 'inline-flex', color: active ? 'var(--accent)' : 'var(--text-3)' }}><I size={18} /></span>
        <span style={{ flex: 1, fontSize: 16 }}>{PHONE_TITLES[id] || id}</span>
        {!onPhone(id) && <span style={{ fontSize: 12, color: 'var(--text-4)', border: '1px solid var(--border)', borderRadius: 999, padding: '1px 8px' }}>Desktop</span>}
      </button>
    );
  };
  return (
    <PhoneSheet open={open} onClose={onClose} title="More" maxHeight="90%">
      {activeUser && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '4px 18px 14px', borderBottom: '1px solid var(--border)' }}>
          <Avatar name={activeUser.full_name} size={40} color={window.hashColor ? window.hashColor(activeUser.email || activeUser.id) : 'var(--accent)'} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{activeUser.full_name}</div>
            <div style={{ fontSize: 13, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {activeUser.email || (activeUser.role || '').replace(/_/g, ' ')}
            </div>
          </div>
        </div>
      )}
      {MORE_GROUPS.filter((g) => (g.admin ? isAdmin : true)).map((g) => {
        const ids = g.ids.filter((id) => (g.admin || canSee(id)) && flagOk(id));
        if (!ids.length) return null;
        return (
          <div key={g.label}>
            <PhoneSectionHeader title={g.label} style={{ padding: '14px 18px 2px' }} />
            {ids.map(row)}
          </div>
        );
      })}
      {canSee('settings') && (
        <div>
          <PhoneSectionHeader title="Workspace" style={{ padding: '14px 18px 2px' }} />
          {row('settings')}
        </div>
      )}
      <PhoneSectionHeader title="Preferences" style={{ padding: '14px 18px 6px' }} />
      <div style={{ padding: '0 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {canSee('deals') && !window.AtlasIsGuest && (
          <div>
            <div style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 6 }}>Team scope</div>
            <PhoneSegmented value={allTeams ? 'all' : 'mine'} onChange={(v) => setScope(v === 'all')}
              options={[{ value: 'mine', label: 'My team' }, { value: 'all', label: 'All teams' }]} />
          </div>
        )}
        <div>
          <div style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 6 }}>Appearance</div>
          <PhoneSegmented value={theme === 'dark' ? 'dark' : 'light'} onChange={(v) => { if ((v === 'dark') !== (theme === 'dark')) onToggleTheme(); }}
            options={[{ value: 'light', label: 'Light' }, { value: 'dark', label: 'Dark' }]} />
        </div>
      </div>
      <div style={{ marginTop: 14, borderTop: '1px solid var(--border)' }}>
        <PhoneActionList items={[
          onSwitchUser && { key: 'switch', label: 'Switch user', sub: 'Admin — view Atlas as a teammate', icon: <Icon.Users size={18} />, onClick: () => setPickUser(true) },
          { key: 'desktop', label: 'Desktop layout', sub: 'Use the full desktop screens on this phone', icon: <Icon.Maximize size={18} />, onClick: () => window.setShellOverride('desktop') },
          { key: 'signout', label: 'Sign out', icon: <Icon.ChevronR size={18} />, danger: true, onClick: onSignOut },
        ]} />
      </div>
      <PhoneSheet open={pickUser} onClose={() => setPickUser(false)} title="Switch user" zIndex={130}>
        <PhoneActionList items={(teamMembers || [])
          .filter((u) => u.active !== false)
          .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''))
          .map((u) => ({
            key: u.id, label: u.full_name, sub: (u.role || '').replace(/_/g, ' '),
            trailing: activeUser && u.id === activeUser.id ? <Icon.Check size={16} color="var(--accent-text)" /> : null,
            onClick: () => { setPickUser(false); onClose(); onSwitchUser(u.id); },
          }))} />
      </PhoneSheet>
    </PhoneSheet>
  );
};

// ── The shell ───────────────────────────────────────────────────────────────
const TABBAR_H = 52;
const CREATE_ITEMS = [
  { kind: 'task', label: 'Task', icon: Icon.Check, page: 'inbox' },
  { kind: 'contact', label: 'Contact', icon: Icon.User, page: 'people' },
  { kind: 'company', label: 'Company', icon: Icon.Building, page: 'companies' },
  { kind: 'engagement', label: 'Engagement', icon: Icon.Pipeline, page: 'deals' },
  { kind: 'buyer_engagement', label: 'Buyer engagement', icon: Icon.Pipeline, page: 'deals' },
];

const PhoneShell = ({
  view, activePage, isDetail, routeKey, pageTitle, canSee, isGuest, isAdmin, flags, activeUser, teamMembers,
  onSwitchUser, theme, onToggleTheme, switchView, backTo, onSearch, onAsk, onCreate, createHints = {}, onSignOut, offline = false, children,
}) => {
  const [moreOpen, setMoreOpen] = React.useState(false);
  const [createOpen, setCreateOpen] = React.useState(false);
  // Overdue + due-today count on the Tasks tab (src/mobile/tasks.jsx).
  const taskBadge = window.useTaskBadge ? window.useTaskBadge(canSee('inbox') && !isGuest ? activeUser : null) : null;

  const tabs = [
    { id: 'home', label: 'Today', icon: Icon.Lightning },
    { id: 'deals', label: 'Deals', icon: Icon.Pipeline },
    { id: 'inbox', label: 'Tasks', icon: Icon.Check },
    { id: 'calendar', label: 'Calendar', icon: Icon.Calendar },
  ].filter((t) => canSee(t.id) && !(isGuest && t.id !== 'deals'));
  // A role that keeps a single page (a deal guest; a caller) has nowhere to
  // tab to — no bar at all; More still reachable for a caller via the title.
  const showTabs = !isGuest && tabs.length > 0;
  const page = activePage || view;
  const tabActive = tabs.some((t) => t.id === page) ? page : 'more';

  // Toasts and sticky footers sit above the tab bar.
  React.useEffect(() => {
    document.documentElement.style.setProperty('--tabbar-h', showTabs ? TABBAR_H + 'px' : '0px');
    return () => document.documentElement.style.setProperty('--tabbar-h', '0px');
  }, [showTabs]);

  // In-app back stack (hash routes are pushState entries): Back steps through
  // history while there is somewhere in-app to go, otherwise it lands on the
  // record's list page — a deep link from Slack still has a way out.
  const stack = React.useRef([]);
  React.useEffect(() => {
    const s = stack.current;
    if (s.length > 1 && s[s.length - 2] === routeKey) s.pop();
    else if (s[s.length - 1] !== routeKey) s.push(routeKey);
    if (s.length > 50) s.splice(0, s.length - 50);
  }, [routeKey]);
  const goBack = () => {
    if (stack.current.length > 1) window.history.back();
    else backTo();
  };

  const onTab = (id) => {
    if (id === 'more') { setMoreOpen(true); return; }
    if (id === view) { window.dispatchEvent(new CustomEvent('atlas-tab-reselect')); return; }
    switchView(id);
  };
  const navigateMore = (id) => { setMoreOpen(false); switchView(id); };

  const title = (isDetail && pageTitle) || PHONE_TITLES[view] || 'Atlas';
  const iconBtn = (label, icon, onClick) => (
    <button type="button" aria-label={label} onClick={onClick}
      style={{ ...tap, width: 44, height: 44, borderRadius: 10, border: 'none', background: 'transparent', color: 'var(--text-2)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
      {icon}
    </button>
  );
  const createItems = CREATE_ITEMS.filter((i) => canSee(i.page));

  return (
    <div className="phone-shell" style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg)' }}>
      <header style={{
        flexShrink: 0, paddingTop: 'var(--sat)', paddingLeft: 'calc(var(--sal) + 4px)', paddingRight: 'calc(var(--sar) + 4px)',
        background: 'var(--surface)', borderBottom: '1px solid var(--border)',
      }}>
        <div style={{ height: 48, display: 'flex', alignItems: 'center', gap: 2 }}>
          {isDetail
            ? iconBtn('Back', <Icon.ChevronL size={22} />, goBack)
            : <span style={{ width: 10, flexShrink: 0 }} />}
          <div style={{ flex: 1, minWidth: 0, fontSize: isDetail ? 16 : 20, fontWeight: isDetail ? 600 : 700, color: 'var(--text)', fontFamily: 'var(--font-display)', letterSpacing: '-0.01em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {title}
          </div>
          {onSearch && iconBtn('Search', <Icon.Search size={20} />, onSearch)}
          {onAsk && iconBtn('Ask Atlas', <Icon.Sparkle size={20} />, onAsk)}
          {onCreate && createItems.length > 0 && iconBtn('Create', <Icon.Plus size={22} />, () => setCreateOpen(true))}
          {!showTabs && !isGuest && iconBtn('More', <Icon.More size={20} />, () => setMoreOpen(true))}
        </div>
      </header>

      {/* Desktop shows "API offline" in the top bar's data-source pill; a phone
          has no such pill, and every page would otherwise just look empty. */}
      {offline && (
        <div role="status" style={{
          flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px 8px 16px',
          background: 'var(--rose-bg)', color: 'var(--rose-fg)', fontSize: 13.5, borderBottom: '1px solid var(--border)',
        }}>
          <span style={{ flex: 1, minWidth: 0 }}>Can't reach Atlas right now — some sections may be empty.</span>
          <button type="button" onClick={() => window.location.reload()}
            style={{ ...tap, height: 32, padding: '0 12px', borderRadius: 8, border: '1px solid currentColor', background: 'transparent', color: 'inherit', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
            Retry
          </button>
        </div>
      )}

      <main style={{ flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden', paddingLeft: 'var(--sal)', paddingRight: 'var(--sar)' }}>
        {children}
      </main>

      {showTabs && (
        <nav style={{
          flexShrink: 0, display: 'flex', background: 'var(--surface)', borderTop: '1px solid var(--border)',
          paddingBottom: 'var(--sab)', paddingLeft: 'var(--sal)', paddingRight: 'var(--sar)',
        }}>
          {[...tabs, { id: 'more', label: 'More', icon: Icon.More }].map((t) => {
            const on = tabActive === t.id;
            const I = t.icon;
            return (
              <button key={t.id} type="button" onClick={() => onTab(t.id)} aria-current={on ? 'page' : undefined}
                style={{
                  ...tap, position: 'relative', flex: 1, height: TABBAR_H, border: 'none', background: 'transparent', cursor: 'pointer',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3,
                  color: on ? 'var(--accent)' : 'var(--text-3)',
                }}>
                <I size={22} />
                {t.id === 'inbox' && taskBadge > 0 && (
                  <span className="num" aria-label={`${taskBadge} due`} style={{
                    position: 'absolute', top: 4, left: '50%', marginLeft: 6, minWidth: 18, height: 18, padding: '0 5px',
                    borderRadius: 9, background: 'var(--rose-fg)', color: '#fff', fontSize: 11, fontWeight: 700,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}>{taskBadge > 99 ? '99+' : taskBadge}</span>
                )}
                <span style={{ fontSize: 10.5, fontWeight: on ? 600 : 500 }}>{t.label}</span>
              </button>
            );
          })}
        </nav>
      )}

      <PhoneMoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} canSee={canSee} isAdmin={isAdmin} flags={flags}
        activeUser={activeUser} teamMembers={teamMembers} onSwitchUser={onSwitchUser} theme={theme} onToggleTheme={onToggleTheme}
        onNavigate={navigateMore} onSignOut={onSignOut} currentView={view} />

      <PhoneSheet open={createOpen} onClose={() => setCreateOpen(false)} title="Create">
        <PhoneActionList items={createItems.map((i) => {
          const I = i.icon;
          return {
            key: i.kind, label: i.label, sub: createHints[i.kind] || null, icon: <I size={18} />,
            onClick: () => { setCreateOpen(false); onCreate(i.kind); },
          };
        })} />
      </PhoneSheet>
    </div>
  );
};

Object.assign(window, {
  PhoneShell, PhoneRoute, PhoneSheet, PhoneActionList, PhoneScroll, PhoneSectionHeader, PhoneRow,
  PhoneSegmented, PhoneChips, PhoneSearchField, PhoneFilterButton, PhoneEmpty, BestOnDesktop, PHONE_TITLES,
});
